import { z } from 'zod';

import { checkupDueSchema } from './checkups';
import { semaforoSchema } from './semaforo';
import {
  introMeetingSchema,
  myPadrinhoSchema,
  padrinhoRequestSchema,
  streamerPadrinhoSchema,
} from './padrinhos';

/**
 * Contrato do Stream Portal (`streaming.growup.gg`) entre a streaming-api, o
 * portal (`streaming-web`) e a seccao Streaming do back-office (`admin-web`).
 * Datas em ISO-8601 (UTC); os ecras mostram-nas em hora de Lisboa.
 *
 * Prefixos HTTP:
 *   - `/api/portal`, `/api/streamers`, `/api/me/...`  portal (publico ou sessao);
 *   - `/api/streaming/...`                            back-office (`streaming.manage`), so em gestao;
 *   - `/internal/streamers/...`                       outras APIs (site-api, eventos).
 */

const datetimeSchema = z.string().datetime({ offset: true });

/** Login de um canal da Twitch: 4 a 25 letras, numeros ou `_`. */
export const twitchLoginSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{4,25}$/, 'nome de canal da Twitch invalido');

/**
 * - `active`: streamer da Grow uP; aparece no portal e inscreve-se em eventos;
 * - `paused`: continua na lista do staff, mas fica fora do portal e dos eventos;
 * - `removed`: saiu. Pode candidatar-se de novo.
 */
export const streamerStatusSchema = z.enum(['active', 'paused', 'removed']);
export type StreamerStatus = z.infer<typeof streamerStatusSchema>;

/**
 * Inatividade (pedida pelo GrowuP a 1-11-2026): um streamer ativo que nao
 * transmite ha `INACTIVE_AFTER_DAYS` dias (ou desde que entrou) sai da pagina
 * publica, mas continua na equipa e volta sozinho a primeira transmissao. A
 * partir de `COMMUNITY_AFTER_DAYS` o Stream Staff pode passa-lo a comunidade.
 * So conta com a Twitch configurada: sem ela nao se sabe quem transmite.
 */
export const INACTIVE_AFTER_DAYS = 60;
export const COMMUNITY_AFTER_DAYS = 90;

/** Como entrou: por candidatura ou adicionado pelo staff (ja era da equipa antes do site). */
export const streamerOriginSchema = z.enum(['application', 'existing']);
export type StreamerOrigin = z.infer<typeof streamerOriginSchema>;

/** Porque saiu: `inactivity` = passado a comunidade por inatividade. */
export const streamerLeftReasonSchema = z.enum(['inactivity']);

export const applicationStatusSchema = z.enum(['pending', 'approved', 'rejected', 'withdrawn']);
export type ApplicationStatus = z.infer<typeof applicationStatusSchema>;

// ---------------------------------------------------------------------------
// Portal
// ---------------------------------------------------------------------------

/** `GET /api/portal`: o que o portal precisa de saber antes de haver sessao. */
export const portalInfoSchema = z.object({
  /** growup.gg (ou o do ambiente): criar conta e ligar a Twitch fazem-se la. */
  siteUrl: z.string().url(),
  /**
   * A Twitch esta configurada (credenciais da app). Com ela, a candidatura
   * exige a ligacao verificada da conta e ha metricas; sem ela, escreve-se o
   * nome do canal e os paineis ficam sem numeros.
   */
  twitch: z.boolean(),
});
export type PortalInfo = z.infer<typeof portalInfoSchema>;

/** O que esta no ar agora, da Twitch. */
export const liveNowSchema = z.object({
  title: z.string(),
  game: z.string().nullable(),
  viewers: z.number().int(),
  startedAt: datetimeSchema,
});
export type LiveNow = z.infer<typeof liveNowSchema>;

/**
 * `GET /api/streamers` e `GET /internal/streamers/public`: os streamers ativos,
 * para a pagina publica `growup.gg/streamers` (pela site-api). Sem dados
 * pessoais.
 */
export const publicStreamerSchema = z.object({
  channel: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
  /** O que transmite, da candidatura aprovada (`null` sem candidatura). */
  content: z.string().nullable(),
  live: liveNowSchema.nullable(),
});
export type PublicStreamer = z.infer<typeof publicStreamerSchema>;

export const applicationInputSchema = z.object({
  /**
   * So conta quando a Twitch nao esta configurada (`portalInfo.twitch`): com
   * ela, o canal vem da ligacao verificada da conta e isto e ignorado.
   */
  channel: twitchLoginSchema.nullable().default(null),
  /** O que transmite: jogos, tipo de conteudo. */
  content: z.string().trim().min(3, 'escreve o que transmites').max(500),
  /** Quantas vezes e quando. */
  schedule: z.string().trim().min(3, 'diz quando costumas transmitir').max(300),
  motivation: z.string().trim().min(20, 'conta-nos um pouco mais (minimo 20 caracteres)').max(2000),
  /** Aceita as regras dos streamers da Grow uP. */
  acceptRules: z.literal(true, {
    errorMap: () => ({ message: 'aceita as regras para continuar' }),
  }),
});
export type ApplicationInput = z.input<typeof applicationInputSchema>;

export const myApplicationSchema = z.object({
  id: z.string().uuid(),
  status: applicationStatusSchema,
  channel: z.string(),
  twitchVerified: z.boolean(),
  content: z.string(),
  schedule: z.string(),
  motivation: z.string(),
  createdAt: datetimeSchema,
  decidedAt: datetimeSchema.nullable(),
  /** Nota do staff ao recusar (ou aprovar). */
  decisionNote: z.string().nullable(),
});
export type MyApplication = z.infer<typeof myApplicationSchema>;

/** Redes da conta que um streamer pode mostrar na pagina publica. */
export const publicLinkSchema = z.enum(['youtube', 'instagram', 'x']);
export type PublicLink = z.infer<typeof publicLinkSchema>;

export const myStreamerSchema = z.object({
  id: z.string().uuid(),
  status: streamerStatusSchema,
  channel: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().url().nullable(),
  joinedAt: datetimeSchema,
  /** Autoriza a foto de perfil da conta em growup.gg/streamers. */
  showPhoto: z.boolean(),
  /** O staff escondeu a foto: aparece a da Twitch. */
  photoHidden: z.boolean(),
  /** Redes da conta que aparecem no cartao publico. */
  showLinks: z.array(publicLinkSchema),
});
export type MyStreamer = z.infer<typeof myStreamerSchema>;

/** `PUT /api/me/public-profile`: o que o streamer mostra em growup.gg/streamers. */
export const publicProfileUpdateSchema = z.object({
  showPhoto: z.boolean(),
  showLinks: z.array(publicLinkSchema).max(3),
});
export type PublicProfileUpdate = z.infer<typeof publicProfileUpdateSchema>;

/** `GET /api/me`: a pessoa no portal. */
export const portalMeSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** A ligacao verificada a Twitch, da conta Grow uP (members-api). */
  twitch: z.object({ login: z.string() }).nullable(),
  /** A ultima candidatura. */
  application: myApplicationSchema.nullable(),
  /** Streamer da Grow uP (qualquer estado menos `removed`). */
  streamer: myStreamerSchema.nullable(),
  /** O padrinho ou madrinha, se for streamer e ja tiver (GS1). */
  padrinho: myPadrinhoSchema.nullable().default(null),
  /** O pedido de outro padrinho, enquanto o Stream Staff nao o trata. */
  padrinhoRequest: padrinhoRequestSchema.nullable().default(null),
});
export type PortalMe = z.infer<typeof portalMeSchema>;

// ---------------------------------------------------------------------------
// Metricas (paineis do streamer e do staff)
// ---------------------------------------------------------------------------

export const dashboardQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(365).default(30),
});

export const streamSessionSchema = z.object({
  startedAt: datetimeSchema,
  endedAt: datetimeSchema.nullable(),
  minutes: z.number().int(),
  title: z.string().nullable(),
  game: z.string().nullable(),
  peakViewers: z.number().int(),
  avgViewers: z.number().int(),
});
export type StreamSession = z.infer<typeof streamSessionSchema>;

export const metricTotalsSchema = z.object({
  sessions: z.number().int(),
  minutesLive: z.number().int(),
  /** Media das medias de cada transmissao, pesada pela duracao. */
  avgViewers: z.number().int(),
  peakViewers: z.number().int(),
  /** Seguidores no ultimo registo do periodo, e a diferenca para o primeiro. */
  followers: z.number().int().nullable(),
  followersChange: z.number().int().nullable(),
});
export type MetricTotals = z.infer<typeof metricTotalsSchema>;

export const dailyPointSchema = z.object({
  /** `AAAA-MM-DD`, Lisboa. */
  date: z.string(),
  minutesLive: z.number().int(),
  peakViewers: z.number().int(),
  followers: z.number().int().nullable(),
});
export type DailyPoint = z.infer<typeof dailyPointSchema>;

/** `GET /api/me/dashboard?days=30` e `GET /api/streaming/streamers/:id/dashboard`. */
export const streamerDashboardSchema = z.object({
  from: z.string(),
  to: z.string(),
  totals: metricTotalsSchema,
  daily: z.array(dailyPointSchema),
  /** As transmissoes do periodo, mais recentes primeiro. */
  sessions: z.array(streamSessionSchema),
  live: liveNowSchema.nullable(),
  /** Ultima leitura da Twitch. `null`: ainda nenhuma (ou Twitch por configurar). */
  syncedAt: datetimeSchema.nullable(),
});
export type StreamerDashboard = z.infer<typeof streamerDashboardSchema>;

/**
 * Historico mensal de um canal (docs/streaming, "Historico do SullyGnome"): os
 * meses antigos vem da recolha unica no SullyGnome; os seguintes, do nosso
 * sync da Twitch. `hasData` falso: o SullyGnome nao tinha o canal nesse mes.
 */
export const historySourceSchema = z.enum(['sullygnome', 'twitch']);
export type HistorySource = z.infer<typeof historySourceSchema>;

export const historyMonthSchema = z.object({
  /** `AAAA-MM`. */
  month: z.string().regex(/^\d{4}-\d{2}$/),
  source: historySourceSchema,
  hasData: z.boolean(),
  minutesLive: z.number().int(),
  streams: z.number().int(),
  avgViewers: z.number().int(),
  peakViewers: z.number().int(),
  /** `null`: sem leituras de seguidores suficientes nesse mes. */
  followersGained: z.number().int().nullable(),
});
export type HistoryMonth = z.infer<typeof historyMonthSchema>;

/** Resumo dos ultimos 30, 90 ou 365 dias no dia da recolha (SullyGnome). */
export const historyRangeSchema = z.object({
  days: z.number().int(),
  hasData: z.boolean(),
  minutesLive: z.number().int(),
  watchMinutes: z.number().int(),
  streams: z.number().int(),
  avgViewers: z.number().int(),
  peakViewers: z.number().int(),
  gamesPlayed: z.number().int(),
  followers: z.number().int(),
  followersGained: z.number().int(),
});
export type HistoryRange = z.infer<typeof historyRangeSchema>;

/** `GET /api/me/history` e `GET /api/streaming/streamers/:id/history`. */
export const streamerHistorySchema = z.object({
  /** Do mais antigo ao mais recente, no maximo 25 (24 completos + o atual). */
  months: z.array(historyMonthSchema),
  ranges: z.array(historyRangeSchema),
  /** Quando veio o SullyGnome. `null`: a recolha ainda nao correu para este canal. */
  importedAt: datetimeSchema.nullable(),
});
export type StreamerHistory = z.infer<typeof streamerHistorySchema>;

// ---------------------------------------------------------------------------
// Back-office (`streaming.manage`)
// ---------------------------------------------------------------------------

/** Nivel na associacao, da members-api, para o staff ver de quem se trata. */
export const applicantLevelSchema = z.enum(['user', 'member', 'associate']);

/**
 * Prazo de resposta a uma candidatura (GS18, decidido pelo GrowuP a
 * 1-11-2026): o candidato sabe-o no email de "recebemos" e o Stream Staff ve
 * a vermelho as que passaram dele.
 */
export const APPLICATION_REPLY_DAYS = 7;

export const adminApplicationSchema = myApplicationSchema.extend({
  accountId: z.string().uuid(),
  login: z.string(),
  email: z.string().nullable(),
  level: applicantLevelSchema.nullable(),
  decidedBy: z.string().nullable(),
  /** Dias inteiros desde que chegou (so nas pendentes; nas decididas, null). */
  waitingDays: z.number().int().nullable(),
  /** Pendente ha mais de `APPLICATION_REPLY_DAYS` dias. */
  overdue: z.boolean(),
});
export type AdminApplication = z.infer<typeof adminApplicationSchema>;

/** O contador no topo da secao Streaming. */
export const applicationSummarySchema = z.object({
  pending: z.number().int(),
  overdue: z.number().int(),
  /** Dias de espera da mais antiga, ou null se nao ha pendentes. */
  oldestDays: z.number().int().nullable(),
});
export type ApplicationSummary = z.infer<typeof applicationSummarySchema>;

export const applicationListQuerySchema = z.object({
  status: applicationStatusSchema.optional(),
});

export const applicationDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z
    .string()
    .trim()
    .max(1000)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
  /** Ao aprovar: o padrinho provisorio (GS1). Opcional. */
  padrinhoId: z.string().uuid().nullable().default(null),
});
export type ApplicationDecision = z.input<typeof applicationDecisionSchema>;

export const adminStreamerSchema = myStreamerSchema.extend({
  accountId: z.string().uuid(),
  login: z.string(),
  email: z.string().nullable(),
  level: applicantLevelSchema.nullable(),
  /** Notas internas do staff. */
  notes: z.string(),
  /** Ultimos 30 dias. */
  last30: metricTotalsSchema,
  live: liveNowSchema.nullable(),
  syncedAt: datetimeSchema.nullable(),
  origin: streamerOriginSchema,
  /** Inicio da ultima transmissao que a Twitch mostrou; `null` se nunca. */
  lastStreamAt: datetimeSchema.nullable(),
  /** Dias desde a ultima transmissao (ou desde que entrou, se nunca transmitiu). */
  daysWithoutStream: z.number().int(),
  /** Ativo e sem transmitir ha `INACTIVE_AFTER_DAYS` dias, com a Twitch configurada. */
  inactive: z.boolean(),
  /** Ja pode passar a comunidade (`COMMUNITY_AFTER_DAYS`). */
  canMoveToCommunity: z.boolean(),
  leftAt: datetimeSchema.nullable(),
  leftReason: streamerLeftReasonSchema.nullable(),
  /** O padrinho ou madrinha (GS1); `null` = sem padrinho. */
  padrinho: streamerPadrinhoSchema.nullable(),
  /** A conversa de apresentacao (RN-GS11), se ja aconteceu. */
  intro: introMeetingSchema.nullable(),
  /** O streamer pediu outro padrinho e o Stream Staff ainda nao tratou. */
  padrinhoRequest: padrinhoRequestSchema.nullable(),
  /** Semaforo de atividade e contacto (GS3). */
  semaforo: semaforoSchema.nullable().default(null),
});
export type AdminStreamer = z.infer<typeof adminStreamerSchema>;

/**
 * Um afilhado, na vista do padrinho (`streaming.padrinho`): o que precisa para
 * o acompanhar, sem o email, o nivel nem as notas internas do Stream Staff.
 */
export const afilhadoSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  displayName: z.string(),
  channel: z.string(),
  avatarUrl: z.string().nullable(),
  status: streamerStatusSchema,
  joinedAt: datetimeSchema,
  /** `false` = provisorio, ate a reuniao mensal. */
  confirmed: z.boolean(),
  since: datetimeSchema,
  intro: introMeetingSchema.nullable(),
  last30: metricTotalsSchema,
  live: liveNowSchema.nullable(),
  lastStreamAt: datetimeSchema.nullable(),
  daysWithoutStream: z.number().int(),
  inactive: z.boolean(),
  /** Mensagens do afilhado por ler pelo padrinho (GS13). */
  unreadMessages: z.number().int().default(0),
  /** O proximo relatorio do padrinho (GS2). */
  checkup: checkupDueSchema.nullable().default(null),
  /** Semaforo de atividade e contacto (GS3). */
  semaforo: semaforoSchema.nullable().default(null),
});
export type Afilhado = z.infer<typeof afilhadoSchema>;

/** `GET /api/streaming/padrinho/me`: os afilhados e o maximo do padrinho. */
export const myAfilhadosSchema = z.object({
  maxAfilhados: z.number().int(),
  afilhados: z.array(afilhadoSchema),
});
export type MyAfilhados = z.infer<typeof myAfilhadosSchema>;

/**
 * `GET /api/streaming/account-search?q=`: contas do site para adicionar um
 * streamer que ja era da equipa. `streamerStatus` diz se ja e streamer.
 */
export const streamerAccountOptionSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  streamerStatus: streamerStatusSchema.nullable(),
});
export type StreamerAccountOption = z.infer<typeof streamerAccountOptionSchema>;

/**
 * `POST /api/streaming/streamers`: o staff adiciona um streamer que ja era da
 * equipa antes do site, sem candidatura. `joinedOn` (`AAAA-MM-DD`) e a data em
 * que entrou na equipa; vazia = hoje. Conta para a camisola de um ano.
 */
export const addExistingStreamerSchema = z.object({
  accountId: z.string().uuid(),
  channel: twitchLoginSchema,
  joinedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'data invalida')
    .nullable()
    .default(null),
});
export type AddExistingStreamer = z.input<typeof addExistingStreamerSchema>;

/** `POST /api/streaming/streamers/:id/community`: passa a comunidade (inatividade). */
export const moveToCommunitySchema = z.object({
  note: z
    .string()
    .trim()
    .max(1000)
    .nullable()
    .default(null)
    .transform((v) => (v === '' ? null : v)),
});
export type MoveToCommunity = z.input<typeof moveToCommunitySchema>;

/**
 * O streamer de uma conta, para a ficha de socio no back-office
 * (`streaming.manage` ou `members.view`). Sem numeros da Twitch: o painel
 * continua so com `streaming.manage`.
 */
export const accountStreamerSchema = myStreamerSchema.extend({
  accountId: z.string().uuid(),
});
export type AccountStreamer = z.infer<typeof accountStreamerSchema>;

/** `GET /api/streaming/accounts/:accountId/streamer`; `null` se a conta nunca foi streamer. */
export const accountStreamerLookupSchema = z.object({
  streamer: accountStreamerSchema.nullable(),
});
export type AccountStreamerLookup = z.infer<typeof accountStreamerLookupSchema>;

export const streamerUpdateSchema = z.object({
  status: streamerStatusSchema,
  notes: z.string().trim().max(2000),
  /** Esconder a foto de perfil na pagina publica (fica a da Twitch). */
  photoHidden: z.boolean().optional(),
});
export type StreamerUpdate = z.infer<typeof streamerUpdateSchema>;

// ---------------------------------------------------------------------------
// Interno
// ---------------------------------------------------------------------------

/**
 * `GET /internal/streamers/by-account/:accountId` (404 se a conta nunca foi
 * streamer). E o contrato que a site-api espera (docs/eventos): so `active` se
 * inscreve como streamer num evento.
 */
export const internalStreamerSchema = z.object({
  streamerId: z.string().uuid(),
  accountId: z.string().uuid(),
  channel: z.string().nullable(),
  status: streamerStatusSchema,
});
export type InternalStreamer = z.infer<typeof internalStreamerSchema>;

/**
 * `GET /internal/streamers/active`: os streamers ativos e desde quando o sao
 * (`joinedAt`, a ultima aprovacao; uma pausa nao a muda). A site-api usa-o para
 * a camisola de streamer ao fim de um ano (docs/loja).
 */
export const internalActiveStreamerSchema = z.object({
  streamerId: z.string().uuid(),
  accountId: z.string().uuid(),
  channel: z.string(),
  displayName: z.string(),
  joinedAt: z.string(),
});
export type InternalActiveStreamer = z.infer<typeof internalActiveStreamerSchema>;

/**
 * `GET /internal/streamers/public`: a lista publica e, so para a site-api, a
 * conta de cada um, se a foto de perfil pode aparecer (autorizada e nao
 * escondida) e que redes mostrar. A site-api tira a conta antes de responder.
 */
export const internalPublicStreamerSchema = publicStreamerSchema.extend({
  accountId: z.string().uuid(),
  showPhoto: z.boolean(),
  showLinks: z.array(publicLinkSchema),
});
export type InternalPublicStreamer = z.infer<typeof internalPublicStreamerSchema>;
